/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
/* eslint-disable no-multi-spaces */

/**
 * The QueryKeys enumeration defines the default, top-level Query Cache Keys.
 *
 * Top-level Query Keys are to be named in all uppercase (underscore delimited)
 * (`ADD_USER`), with their corresponding values are to be all lowercase (dash
 * delimited) (`'add-user'`).
 *
 * All Query Keys passed to React Query APIs are to be wrapped in an Array,
 * and fine grained keys are to be based on the top-level keys with additional
 * implementation specific keys provided which are specific to the underlying
 * query.
 *
 * ```typescript
 *
 *  import {QueryKeys, createKey} from '../config';
 *
 *  export const useGetUsers = (accountId: number, paging: Paging): UseGetUsersResult => {
 *
 *    const query = useQuery([QueryKeys.USERS_GET, accountId, paging.page], getUsers, {
 *      ...getStaleTime(5),
 *      keepPreviousData: true
 *    });
 *  ...
 * }
 *
 * ```
 *
 */
export enum QueryKey {
  LOGIN                     = 'login',
  LOGOUT                    = 'logout',
  CURRENT_USER              = 'current-user',
  USER                      = 'user',
  GET_USER_OPERATOR         = 'get-user-operator',
  GET_NOTIFICATIONS_EVENTS  = 'get-notifications-events',
  GET_INSPECTIONS_RESULT    = 'get-inspections-result',
  GET_INSPECTIONS_RESULTS   = 'get-inspectations-results',
  GET_INSPECTIONS_FORMS     = 'get-inspectations-form-options',
  GET_INSPECTIONS_STATUS    = 'get-inspectations-status-options',
  GET_INSPECTIONS_VEHICLES  = 'get-inspectations-vehicle-options',
  GET_CRASH_EVENTS          = 'get-crash-events',
  GET_TRIPS                 = 'get-trips',
  GET_INSPECTIONS_TEMPLATES = 'get-inspections-templates',
  GET_INSPECTIONS_ITEMS     = 'get-inspections-template-items',
  GET_INSPECTIONS_GROUPS    = 'get-inspections-template-groups',
  GET_MAINTENANCE_DATA      = 'get-maintenance-data',
  GET_MAINTENANCE_HISTORY   = 'get-maintenance-history',
  GET_MAINTENANCE_SERVICE   = 'get-maintenance-service',
  GET_GEOFENCE_EVENTS       = 'get-geofence-events',
  DEVICE_TYPES              = 'device-types',
  GET_ASSET                 = 'get-asset',
  GET_ALL_DRIVERS           = 'get-all-drivers',
  ASSET_HISTORY             = 'asset-history',
  HEALTH_EVENTS             = 'health-events',
  SWAP_DEVICES              = 'device-swap',
  DEVICE_GET                = 'device-get',
  DEVICE_GET_BY_ESN         = 'device-get-by-esn',
  DOOR_SENSOR_DATA          = 'door-sensor-data',
  BRAKE_SENSOR_DATA         = 'brake-sensor-data',
  CURRENT_FILE_HISTORY      = 'current-file-history',
  PENDING_FILE_HISTORY      = 'pending-file-history',
  LOAD_OFFLINE_CAN_REQUESTS = 'load-offline-can-requests',
  SENSOR_DATA               = 'sensor-data',
  HARDWARE_PARTS            = 'hardware-parts',
  SC_DEVICE_SENSOR          = 'sc-device-sensor',
  SC_DEVICE_FIELDS          = 'sc-device-fields',
  DEVICE_HEART_BEAT         = 'device-heart-beat',
  GET_DEVICES_FILTER        = 'get-devices-filter',
  NOTIFICATION_EVENTS       = 'get-notification-events',
  GET_EVENT_STATUS          = 'get-event-status',
  GEOFENCE_EVENTS           = 'geofence-events',
  IONVISION_EVENTS          = 'ionVision-events',
  VIDEO_EVENTS              = 'video-events',
  CRASHBOXX_EVENTS          = 'crashboxx-events',
  PEG_LABELS                = 'peg-labels',
  SYSTEM_OF_UNITS           = 'system-of-units',
  PEG_BEHAVIORS             = 'peg-behaviors',
  SHIPMENT_HISTORY          = 'shipment-history',
  CREATE_JOB                = 'create-job',
  JOB_STATUS                = 'job-status',
  ACCOUNT_APPLICATIONS      = 'account-applications',
  DEVICE_FILES              = 'device-files',
  ADD_HARDWARE_PART         = 'add-hardware-part',
  GET_ARCH_CATEGORIES       = 'get-arch-categories',
  UPDATE_HARDWARE_PART      = 'update-hardware-part',
  HARDWARE_PLATFORM         = 'hardware-platform',
  HARDWARE_STATE            = 'hardware-state',
  HARDWARE_APPLICATION      = 'hardware-application',
  HARDWARE_CARRIERS         = 'carriers',
  DEVICE_SOURCES            = 'device-sources',
  LOAD_GROUPS               = 'load-groups',
  LOAD_ASSETS               = 'load-assets',
  LOAD_ASSET_UPDATES        = 'load-asset-updates',
  LOAD_HVS_ASSETS           = 'load_hvs_assets',
  LOAD_HVS_ASSETS_UPDATES   = 'load_hvs_assets_updates',
  LOAD_GEOFENCES            = 'load-geofences',
  LOAD_GEOFENCE_UPDATES     = 'load-geofence-updates',
  GET_PERMISSIONS           = 'get-permissions',
  DEVICE_MANUFACTURING_INFO = 'device-manufacturing-info',
  DEVICE_SHIPPING_INFO      = 'device-shipping-info',
  DEVICE_SENTINEL_INFO      = 'devce-sentinel-info',
  ADD_SSO_CONFIGURATION     = 'add-sso-configuration',
  UPDATE_SSO_CONFIGURATION  = 'update-sso-configuration',
  DELETE_SSO_CONFIGURATION  = 'delete-sso-configuration',
  GET_SSO_CONFIGURATION     = 'get-sso-configuration',
  GET_JOB                   = 'get-job',
  LOAD_JOB_ID               = 'load-job-id',
  DEVICE_APP_IDS            = 'device-app-ids',
  VISION_INFINITE_EVENTS    = 'vision-infinite-events',
  EXPORT_LARGE_ENTITIES     = 'export-large-entities',
  APPID_PLATFORM_TYPES      = 'appid-platform-types',
  APPLICATION_IDS           = 'applicationIds',
  COMPLIANCE_DASHBOARD      = 'complianceDashboard',
  TEST_HISTORY              = 'testHistory',
  DEVICE_PART_NUMBERS       = 'devicePartNumbers',
  GET_APPLICATION           = 'get-application',
  GET_ACCOUNT               = 'get-account',
  GET_ALL_DEVICES           = 'get-all-devices',
  UPDATE_INVENTORY_DEVICE   = 'update-inventory-device',
  DELETE_INVENTORY_DEVICE   = 'delete-inventory-device',
  GENERIC_SENSOR_DATA       = 'generic-sensor-data',
  NOTIFICATION_TYPES        = 'get-notification-types',
  ACCOUNT_SUBSCRIPTION      = 'account-subscription',
  ACCOUNT_FEATURES          = 'account-features',
  ACCOUNT_ANCESTOR_FEATURES = 'account-ancestor-features',
  SEARCH_ROLES              = 'search-roles',
  ASSET_SERVICE_STATUS      = 'asset-service-status',
  GET_DTC_CODES             = 'get-dtc-codes',
  ADD_DEVICE_PART_NUMBERS   = 'add-device-part-number',
  MANUFACTURING_CARRIERS    = 'manufacturing-carriers',
  GET_CVS_ASSETS            = 'get-cvs-assets',
  GET_CVS_ASSETS_IDS        = 'get-cvs-asset-ids',
  GET_CVS_ASSETS_COUNT      = 'get-cvs-assets-count',
  GET_SIDE_SHEET_CVS_ASSETS = 'get-side-sheet-cvs-assets',
  GET_SIDE_SHEET_CVS_ASSETS_UPDATES = 'get_side_sheet_cvs_assets_updates',
  DVR_SEARCH                        = 'dvr-search',
  FILE_HISTORY_CURRENT              = 'file-history-current',
  MANUFACTURING_AUX_VALUES          = 'manufacturing-auxiliary-values',
  EDIT_DEVICE_PART_NUMBERS          = 'edit-device-part-number',
  DELETE_DEVICE_PART_NUMBER         = 'delete-device-part-number',
  MANUFACTURED_DEVICES              = 'manufactured-devices',
  RESEND_SHIPMENT                   = 'resend-shipment',
  MESSAGE_ENDPOINT                  = 'message-endpoint',
  FILE_DEVICE_COUNT                 = 'file-device-count',
  GET_SFTP_CONFIGURATIONS           = 'get-sftp-configurations',
  GET_SUB_ACCOUNTS                  = 'get-sub-accounts',
  JOBS_BY_NAME                      = 'jobs-by-name',
  UNPAIRED_DEVICE_REPORT            = 'unpaired-device-reports',
  UNPAIRED_ASSETS_REPORT            = 'unpaired-assets-reports',
  DEVICE_COMMUNICATION_REPORT       = 'device-communication-report',
  SCHEDULED_REPORTS                 = 'scheduledReports',
  MIGRATION_RESULTS                 = 'migration-results',
  MIGRATION_DETAILS                 = 'migration-details',
  GROUPS_SEARCH                     = 'groups-search',
  PLATFORMS                         = 'platforms',
  AUTOJOBS                          = 'autojobs',
  JOB_SEARCH_ENGINE                 = 'job-search-engine',
  LOAD_TASK_DEVICES                 = 'load-task-devices',
  ANCESTORS_ACCOUNT_LIST            = 'ancestorsAccountList',
  OTA_STATE                         = 'otaState',
  GET_ALL_INSPECTIONS_GROUPS        = 'get-all-inspections-groups',
  GET_ALL_INSPECTIONS_ITEMS         = 'get-all-inspections-items',
  GET_GROUPS_IN_TEMPLATE            = 'get-groups-in-template',
  GET_TEMPLATE_PUBLISH_HISTORY      = 'get-template-publish-history',
  PROFILE_MANAGER                   = 'profile-manager',
  PROFILE_MANAGER_BY_ID             = 'associated-profile-search',
  LOAD_VALIDATION_RESULTS           = 'validation-results',
  RANGE_CHECK_RULE                  = 'range-check-rule',
  VEHICLE_MANAGER                   = 'vehicle-manager',
  VEHICLE_CONNECTION_STATUS         = 'vehicle-connection-status',
  VEHICLE_SCOUT_CAPABLE             = 'vehicle-scout-capable',
  GET_SERVICE_ITEM                  = 'getServiceItem',
  RERUN_JOB                         = 'rerun-job',
  CANCEL_JOB                        = 'cancel-job',
  CANCEL_TASK                       = 'cancel-task',
  CANCEL_TASK_DEVICE                = 'cancel-task-device',
  LOAD_VEHICLE_HISTORY              = 'vehicle-history',
  SAVE_VALIDATION_RULE              = 'save-validation-rule',
  VALIDATE_OEM_RULE                 = 'validate-oem-rule',
  MODBUS_PARAMETER                  = 'modbus-parameter',
  ASSETS_INFINITE                   = 'assets-infinite',
  TPMS_DATA                         = 'tpms-data',
  LATEST_AVL_EVENT                  = 'latest-avl-event',
  ASSET_QUERY                       = 'asset-query',
  TPMS_ASSIGNMENT                   = 'tpms-assignment',
  PROFILE_VEHICLES                  = 'profile-vehicles',
  VEHICLE_INFO_BY_VIN               = 'vehicle-info',
  VEHICLE_PROFILE_STATUS            = 'vehicle-profile-status',
  VEHICLE_PROFILE_ENUM              = 'vehicle-profile-enum',
  VEHICLE_PROFILE_MATCH             = 'vehicle-profile-match',
  CREATE_VEHICLE_PROFILE            = 'create-vehicle-profile',
  UPDATE_VEHICLE_PROFILE            = 'update-vehicle-profile',
  LOAD_VEHICLE_PROFILE              = 'load-vehicle-profile',
  DELETE_VEHICLE_PROFILE            = 'delete-vehicle-profile',
  GET_VEHICLE_PROFILE               = 'get-vehicle-profile',
  SISENSE_DASHBOARD_SEARCH          = 'sisense-dashboard-search',
  CARRIER_PART_NUMBERS              = 'carrier-part-numbers',
  CREATE_CARRIER_PARTNUMBER         = 'create-carrier-partnumber',
  UPDATE_CARRIER_PARTNUMBER         = 'update-carrier-partnumber',
  DELETE_CARRIER_PARTNUMBER         = 'delete-carrier-partnumber',
  DEVICE_EXTENDED_ATTRIBUTES        = 'get-device-ext-attribs',
  VEHICLE_PROFILE_METADATA          = 'vehicle-profile-metadata',
  VIN_DECODER                       = 'vin-decoder',
  BUS_PROFILES                      = 'bus-profiles',
  FETCH_JOBS                        = 'fetch-jobs',
  PROFILE_BY_ID                     = 'profile-by-id',
  EMISSION_TESTER_BY_ID             = 'emission-tester-by-id',
  UPLOAD_EMISSION_TESTER            = 'upload-emission-tester',
  ASSET_BY_DEVICE_ID                = 'asset-by-device-id',
  DEVICE_BY_DEVICE_ID               = 'device-by-device-id',
  CREATE_ONLINE_ALERT               = 'create-online-alert',
  ALERT_HISTORY                     = 'alert-history',
  PEG2_DEVICE_MODELS                = 'peg2DeviceModels',
  PEG2_VEHICLE_TYPES                = 'peg2VehicleTypes',
  PEG2_MODULE_PROFILES              = 'peg2ModuleProfiles',
  PEG2_MODULE_PROFILE               = 'peg2ModuleProfile',
  PEG2_SCRIPT_ASSEMBLY              = 'peg2ScriptAssembly',
  PEG_WIZARD_METADATA               = 'pegWizardMetadata',
  DELETE_TESTER                     = 'delete-tester',
  VEHICLE_DISCOVERY_SEARCH          = 'vehicle-discovery-search',
  VEHICLE_DISCOVERY_AGGREGATIONS    = 'vehicle-discovery-aggregations',
  VEHICLE_DISCOVERY_DEVICE_ATTRIBS  = 'vehicle-discovery-device-attribs',
  VEHICLE_DISCOVERY_DEVICE_IDS      = 'vehicle-discovery-device-ids',
  VEHICLE_DISCOVERY_LINKED_JOBS     = 'vehicle-discovery-linked-jobs'
};

/**
 * Provides a convenience function which creates a query key array based on
 * the provide QueryKey and arbitrary values.
 *
 * ```typescript
 *
 *  import {QueryKeys, createKey} from '../config';
 *
 *  export const useGetUsers = (accountId: number, paging: Paging): UseGetUsersResult => {
 *
 *    const keys = useMemo(() => (
 *      createKey(QueryKeys.USERS_GET, accountId, paging.page)
 *    ),[accountId, paging.page]);
 *
 *    const query = useQuery(keys, getUsers, {
 *      ...getStaleTime(5),
 *      keepPreviousData: true
 *    });
 *  ...
 * }
 * ```
 *
 */
export const createKey = (key: QueryKey, ...args): any[] => {
  return [key, ...args];
};

export default QueryKey;
